/**
 * DashboardPanel — the CEO view: KPIs, cash trajectory, market conditions,
 * channel health, active modifiers, and the latest headlines.
 */

import { type ReactNode } from 'react';
import { AlertTriangle, Flame, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { KpiCard, Meter, PanelSection } from '@/components/game/ui';
import { MattressArt } from '@/components/game/art/MattressArt';
import { useGameStore } from '@/store/gameStore';
import { money, num, pct, formatGameDate } from '@/game/format';
import {
  blendedReturnRate,
  avgRating,
  avgQuality,
  fulfillmentPerUnit,
  freightMultiplier,
  outboundParcelPerUnit,
  sleepScore,
  activeModifiers,
  cacEffective,
} from '@/game/economy';
import { TARIFF_LABELS, TUNING } from '@/game/constants';
import { totalInventory, warehouseCapacity } from '@/game/engine';

export function DashboardPanel() {
  const state = useGameStore((s) => s.state)!;

  const daily = state.history.daily;
  const revSeries = daily.slice(-60).map((d) => d.rev);
  const cashSeries = daily.slice(-90).map((d) => d.cash);
  const netSeries = daily.slice(-30).map((d) => d.net);

  const last7 = daily.slice(-7);
  const prior7 = daily.slice(-14, -7);
  const rev7 = last7.reduce((a, d) => a + d.rev, 0);
  const revPrior7 = prior7.reduce((a, d) => a + d.rev, 0);
  const revDelta = revPrior7 > 0 ? ((rev7 - revPrior7) / revPrior7) * 100 : undefined;
  const netToday = daily.length ? daily[daily.length - 1].net : 0;
  const unitsYesterday = daily.length ? daily[daily.length - 1].units : 0;

  const inv = totalInventory(state);
  const cap = warehouseCapacity(state);
  const invPctFull = cap > 0 ? (inv / cap) * 100 : 0;
  const runwayDays =
    netSeries.length > 0
      ? Math.floor(state.player.cash / Math.min(-1, netSeries.reduce((a, b) => a + b, 0) / netSeries.length))
      : Infinity;
  const lowStock = state.products.filter((p) => p.status === 'active' && (state.inventory[p.id] ?? 0) <= state.pos.reduce((a, po) => a + (po.productId === p.id ? po.units : 0), 0));

  const mods = activeModifiers(state);
  const fm = freightMultiplier(state);
  const parcel = outboundParcelPerUnit(state);
  const fba = fulfillmentPerUnit(state);
  const rr = blendedReturnRate(state);
  const metaCac = cacEffective(state, 'meta');
  const flagship = state.products
    .filter((p) => p.status === 'active')
    .sort((a, b) => b.unitsSoldTotal - a.unitsSoldTotal)[0];

  return (
    <div className="space-y-6">
      {/* Headline KPIs */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard
          label="Cash"
          value={money(state.player.cash)}
          sub={`${money(netToday)} today`}
          spark={cashSeries}
        />
        <KpiCard
          label="Revenue (7d)"
          value={money(rev7)}
          delta={revDelta}
          sub="vs prior week"
          spark={revSeries}
        />
        <KpiCard label="Units yesterday" value={num(unitsYesterday)} sub={`${pct(rr)} blended return rate`} />
        <KpiCard
          label="Sleep Score"
          value={sleepScore(state)}
          sub={`★ ${avgRating(state).toFixed(2)} · Q${Math.round(avgQuality(state))}`}
        />
      </div>

      {(lowStock.length > 0 || state.channels.amazon.suspended) && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="flex flex-wrap items-center gap-2 py-3 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            {state.channels.amazon.suspended && <span className="font-medium text-red-600 dark:text-red-400">Amazon account suspended — sales paused.</span>}
            {lowStock.length > 0 && (
              <span className="text-muted-foreground">
                Low/no stock: {lowStock.map((p) => p.name).join(', ')}. Rank decays while out of stock.
              </span>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Brand health */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Brand Health</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Meter label="Brand awareness" value={state.player.awareness} colorClass="bg-sky-500" />
            <Meter label="Reputation" value={state.player.reputation} colorClass="bg-emerald-500" />
            <Meter
              label={`Warehouse ${inv.toLocaleString()}/${cap.toLocaleString()} units`}
              value={invPctFull}
              colorClass={invPctFull > 85 ? 'bg-red-500' : 'bg-amber-500'}
              valueText={`${Math.round(invPctFull)}%`}
            />
            <p className="text-xs text-muted-foreground">
              Runway:{' '}
              {Number.isFinite(runwayDays)
                ? runwayDays >= 9999
                  ? 'cash-positive trajectory'
                  : `${runwayDays} days at current burn`
                : 'no burn data yet'}{' '}
              · Email list {num(state.marketing.emailListSize)}
            </p>
            {flagship && (
              <div className="flex items-center gap-3 border-t pt-3">
                <MattressArt archetype={flagship.archetype} quality={flagship.quality} coolingScore={flagship.coolingScore} seed={flagship.id} className="w-20 shrink-0" />
                <div className="min-w-0 text-xs">
                  <p className="truncate font-medium">Flagship: {flagship.name}</p>
                  <p className="text-muted-foreground">
                    ★ {avgRating(state).toFixed(2)} · {num(flagship.unitsSoldTotal)} sold · Q{Math.round(flagship.quality)} spec
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Market conditions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Market Conditions
              {state.world.oil > 95 ? <Flame className="h-4 w-4 text-orange-500" /> : null}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ConditionRow
              icon={state.world.oilLagged > 85 ? <TrendingUp className="h-4 w-4 text-orange-500" /> : <TrendingDown className="h-4 w-4 text-emerald-600" />}
              label={`WTI $${state.world.oil.toFixed(0)} (lagged avg $${state.world.oilLagged.toFixed(0)})`}
              value={`freight ×${fm.toFixed(2)}`}
            />
            <ConditionRow label="Outbound parcel / unit" value={money(parcel)} />
            <ConditionRow label="FBA fulfillment / unit" value={money(fba)} />
            <ConditionRow label="Foam cost index" value={`${(state.world.foamIndex).toFixed(0)} (${state.world.foamIndex > 110 ? 'elevated' : 'normal'})`} />
            <ConditionRow
              label="Competitive pressure"
              value={state.world.competition > 1.25 ? 'bloodbath' : state.world.competition > 1.0 ? 'hot' : 'manageable'}
              warn={state.world.competition > 1.25}
            />
            <ConditionRow label="Blended Meta CAC" value={money(metaCac)} />
          </CardContent>
        </Card>
      </div>

      {/* Tariffs */}
      <PanelSection title="Duty Exposure by Origin" desc="Effective landed-cost multipliers. Trade events move these — watch China.">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {Object.entries(state.tariffs).map(([cc, mult]) => {
            const m = mult as number;
            return (
              <div key={cc} className={cn('rounded-lg border bg-card p-3 text-center', m >= 1.8 ? 'border-red-500/40' : m >= 1.15 ? 'border-amber-500/40' : '')}>
                <p className="text-xs text-muted-foreground">{TARIFF_LABELS[cc as keyof typeof TARIFF_LABELS]}</p>
                <p className={cn('mt-1 font-semibold tabular-nums', m >= 1.8 ? 'text-red-600 dark:text-red-400' : m >= 1.15 ? 'text-amber-600 dark:text-amber-400' : '')}>
                  ×{m.toFixed(2)}
                </p>
              </div>
            );
          })}
        </div>
      </PanelSection>

      {/* Active modifiers */}
      {mods.length > 0 && (
        <PanelSection title="Active Effects" desc="Temporary forces acting on your business right now.">
          <div className="flex flex-wrap gap-2">
            {mods.map((m) => (
              <Badge key={m.id} variant="outline" className="max-w-full gap-1 whitespace-normal text-left">
                <span>{m.label}</span>
                <span className="text-[10px] text-muted-foreground">→ {formatGameDate(m.expiresDay)}</span>
              </Badge>
            ))}
          </div>
        </PanelSection>
      )}

      {/* Headlines */}
      <PanelSection title="The Wire" desc={`Latest industry noise. Next acquisition window ~day ${TUNING.exitOfferFirstDay}.`}>
        <ul className="divide-y rounded-xl border bg-card">
          {state.news.slice(0, 6).map((n) => (
            <li key={n.id} className="px-4 py-2.5 text-sm">
              <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: severityColor(n.severity) }} />
              <span className="font-medium">{n.headline}</span>
              <span className="ml-2 text-xs text-muted-foreground">{formatGameDate(n.day)}</span>
            </li>
          ))}
        </ul>
      </PanelSection>
    </div>
  );
}

function ConditionRow({ icon, label, value, warn }: { icon?: ReactNode; label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-2 text-muted-foreground">{icon}{label}</span>
      <span className={cn('font-medium tabular-nums', warn && 'text-red-600 dark:text-red-400')}>{value}</span>
    </div>
  );
}

function severityColor(sev: number): string {
  if (sev >= 4) return '#ef4444';
  if (sev === 3) return '#f59e0b';
  if (sev === 2) return '#3b82f6';
  return '#10b981';
}
